import type { GetServerSideProps, NextPage } from "next";
import dynamic from "next/dynamic";
import { SignInModalStyles } from "~/components/auth/SignInModalStyles";
import { BETA_WORKSPACE_PATH } from "~/lib/beta";
import { betaAccess } from "~/server/betaAccess";
import { wallSerif } from "~/styles/fonts/signInWall";
import HeadComponent from "~/ui/head";

// The standalone beta verifies email through Clerk without a member lookup.
//
// The `beta` gate (src/server/flags.ts) is read here with the visitor's Clerk
// email or their TWT member session (src/server/betaAccess.ts). While it is
// off for them the page answers like every held page. A visitor who is
// already signed in goes straight to the workspace.

// The sign-in screen loads as its own chunk with its ClerkProvider inside, as
// the dashboard wall's panel does, so the Clerk SDK stays out of the shared
// _app chunk.
const BetaSignIn = dynamic(
  () => import("~/components/beta/BetaSignIn").then((mod) => mod.BetaSignIn),
  { loading: () => null }
);

type BetaPageProps = { accessIssue: "verify_email" | "not_eligible" | null };

export const getServerSideProps: GetServerSideProps<BetaPageProps> = async (context) => {
  context.res.setHeader("Cache-Control", "private, no-store");
  const access = await betaAccess(context.req, context.res);
  if (!access.open) {
    return { redirect: { destination: "/launch-unavailable", permanent: false } };
  }
  if (access.visitor?.hasAccess) {
    return { redirect: { destination: BETA_WORKSPACE_PATH, permanent: false } };
  }
  return { props: { accessIssue: access.accessIssue ?? null } };
};

const BetaPage: NextPage<BetaPageProps> = ({ accessIssue }) => {
  return (
    <>
      <HeadComponent title="TWT | Beta" />
      <main className={`whop-login-page twt-signin-dark ${wallSerif.variable}`}>
        <SignInModalStyles />
        <section className="whop-login-stage">
          <BetaSignIn next={BETA_WORKSPACE_PATH} accessIssue={accessIssue} />
        </section>
      </main>
      <style jsx global>{`
        /* The whole page is the dark sheet's room: one flat color behind the sheet. */
        .whop-login-page.twt-signin-dark {
          --twt-modal-page: #0f0f0e;
        }
      `}</style>
    </>
  );
};

export default BetaPage;
